import * as Yup from 'yup';
import { isValidPhoneNumber } from 'react-phone-number-input';

import {
  FormField,
  InputOptions,
  RadioOptions,
  FormFieldType,
  SelectOptions,
  ScaleRatingOptions,
} from 'types';

import { passwordPattern } from './regex';

export const advancedPassword = Yup.string()
  .trim()
  .matches(
    passwordPattern,
    'Password should contain minimum 8 characters with one numeric, one lowercase and one uppercase character'
  )
  .required();

export const confirmPassword = (schema: Yup.StringSchema) =>
  schema.test('passwordRepeat', 'Passwords must match', function (value) {
    return this.parent.password === value;
  });

export const generateFormFieldsValidationSchema = (
  fields: FormField[]
): Yup.ObjectSchema<any> => {
  let schemaFields: Record<string, Yup.AnySchema> = {};

  fields.forEach((field) => {
    schemaFields = createFieldValidation(field, schemaFields);
  });

  return Yup.object().shape(schemaFields);
};

const createFieldValidation = (
  field: FormField,
  schemaFields: Record<string, Yup.AnySchema>
): Record<string, Yup.AnySchema> => {
  if (field.type === FormFieldType.FIELD_ARRAY) {
    let schema = {
      ...schemaFields,
    };
    field.options?.fields.forEach((field) => {
      schema = createFieldValidation(field, schema);
    });
    return schema;
  }

  let validation: Yup.AnySchema;

  switch (field.type) {
    case FormFieldType.INPUT:
      validation = createInputValidation(field.options as InputOptions);
      break;

    case FormFieldType.SELECT:
      validation = createSelectValidation(field.options as SelectOptions);
      break;

    case FormFieldType.RADIO:
      validation = createRadioValidation(field.options as RadioOptions);
      break;

    case FormFieldType.SCALE_RATING:
      validation = createScaleRatingValidation(
        field.options as ScaleRatingOptions,
        field.required
      );
      break;

    case FormFieldType.PAGE_BREAK:
      validation = Yup.object();
      break;
  }

  if (field.required) {
    validation = validation.required('Field is required');
  } else {
    validation = validation.nullable().optional();
  }

  return {
    ...schemaFields,
    [field.id]: validation,
  };
};

const createInputValidation = (options?: InputOptions): Yup.AnySchema => {
  if (!options) {
    return Yup.string();
  }

  let validation: Yup.StringSchema | Yup.NumberSchema;

  if (options.type === 'number') {
    validation = Yup.number().typeError('Must be a number');

    if (options.minValue !== undefined) {
      validation = validation.min(
        options.minValue,
        `Must be at least ${options.minValue}`
      );
    }

    if (options.maxValue !== undefined) {
      validation = validation.max(
        options.maxValue,
        `Must be at most ${options.maxValue}`
      );
    }

    if (options.integer) {
      validation = validation.integer('Must be an integer');
    }
  } else {
    validation = Yup.string();

    if (options.type === 'email') {
      validation = validation.email('Must be a valid email address');
    }

    if (options.minLength !== undefined) {
      validation = validation.min(
        options.minLength,
        `Must be at least ${options.minLength} characters`
      );
    }

    if (options.maxLength !== undefined) {
      validation = validation.max(
        options.maxLength,
        `Must be at most ${options.maxLength} characters`
      );
    }
  }

  return validation;
};

const createSelectValidation = (options?: SelectOptions): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options)) {
    return Yup.string();
  }
  if (options.multiple) {
    return Yup.array()
      .of(Yup.string().oneOf(options.options, 'Invalid option selected'))
      .min(1, 'At least one option must be selected');
  } else {
    return Yup.string().oneOf(
      options.options,
      `Must be one of: ${options.options.join(', ')}`
    );
  }
};

const createRadioValidation = (options?: RadioOptions): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options)) {
    return Yup.string();
  }
  return Yup.string().oneOf(
    options.options,
    `Must be one of: ${options.options.join(', ')}`
  );
};

const createScaleRatingValidation = (
  options?: ScaleRatingOptions,
  isRequired?: boolean
): Yup.AnySchema => {
  if (!options || !Array.isArray(options.options) || !options.options.length) {
    return Yup.object();
  }
  const { minValue, maxValue } = options;

  return Yup.array().of(
    Yup.number()
      .min(minValue, `Rating must be at least ${minValue}`)
      .max(maxValue, `Rating must be at most ${maxValue}`)
      .when([], {
        is: () => isRequired,
        otherwise: (schema) => schema.nullable().optional(),
        then: (schema) => schema.required(`Rating is required`),
      })
  );
};

export const passwordValidationRules: Record<
  string,
  (value: string) => boolean
> = {
  '8 singles': (value) => value.length >= 8,
  'Capital letters': (value) => /^(?=.*[A-Z])/.test(value),
  'Lowercase letters': (value) => /^(?=.*[a-z])/.test(value),
  'Number in password': (value) => /^(?=.*[0-9])/.test(value),
};

export const phoneNumber = (name: string) =>
  Yup.string()
    .nullable()
    .test(name, 'Invalid phone number', function (value) {
      return value && value.length > 0 ? isValidPhoneNumber(value) : true;
    });

export const validateZipCode = (isZipRequired: boolean = true) => {
  let zipCodeSchema = Yup.string()
    .nullable()
    .test('length', 'Zip code must be 5 or 9 digits', (val) => {
      if (!val) {
        return !isZipRequired;
      }

      return val.length === 5 || val.length === 9;
    });

  if (isZipRequired) {
    zipCodeSchema = zipCodeSchema.required();
  }

  return zipCodeSchema;
};

Yup.addMethod(
  Yup.string,
  'alphanumericWithBasicPunctuation',
  function (message) {
    return this.matches(/^[A-Za-z0-9 .'-]*$/, {
      excludeEmptyString: true,
      message:
        message ||
        'Please use only letters, numbers, spaces, and basic punctuation',
    });
  }
);
